import { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  ArrowLeft, Plus, Pencil, Trash2, Car, Bike, Search,
  Loader2, Download, Upload, Truck, ChevronLeft, ChevronRight
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const LOGO_URL = "https://customer-assets.emergentagent.com/job_yolo-crop-counter/artifacts/dqftlupc_Recurso%2021VR%20logos.png";
const DISRUPTIVE_LOGO = "https://customer-assets.emergentagent.com/job_yolo-crop-counter/artifacts/qx3y7mcn_Recurso%202lgo%20disr.png";
const PAGE_SIZE = 20;

export default function VehiclesPage() {
  const [vehicles, setVehicles] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isImporting, setIsImporting] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const fileInputRef = useRef(null);

  const [form, setForm] = useState({ plate: '', vehicle_type: 'auto', owner_name: '', phone: '', notes: '' });

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const { data } = await axios.get(`${API}/generic-vehicles`, {
        withCredentials: true,
        params: {
          page,
          page_size: PAGE_SIZE,
          ...(searchTerm.trim() ? { search: searchTerm.trim() } : {}),
        },
      });
      setVehicles(data.items);
      setTotal(data.total);
      setTotalPages(data.total_pages);
      if (data.page !== page) setPage(data.page);
    } catch (err) {
      toast.error('Error al cargar vehiculos');
    } finally {
      setIsLoading(false);
    }
  }, [page, searchTerm]);

  useEffect(() => {
    const timeout = setTimeout(fetchData, 250);
    return () => clearTimeout(timeout);
  }, [fetchData]);

  const openCreate = () => {
    setEditingVehicle(null);
    setForm({ plate: '', vehicle_type: 'auto', owner_name: '', phone: '', notes: '' });
    setShowModal(true);
  };

  const openEdit = (v) => {
    setEditingVehicle(v);
    setForm({ plate: v.plate, vehicle_type: v.vehicle_type, owner_name: v.owner_name, phone: v.phone, notes: v.notes });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.plate) { toast.error('La patente es obligatoria'); return; }
    setIsSaving(true);
    try {
      if (editingVehicle) {
        await axios.put(`${API}/generic-vehicles/${editingVehicle.id}`, form, { withCredentials: true });
        toast.success('Vehiculo actualizado');
      } else {
        await axios.post(`${API}/generic-vehicles`, form, { withCredentials: true });
        toast.success('Vehiculo agregado');
      }
      setShowModal(false);
      if (!editingVehicle && page !== 1) setPage(1);
      else fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminar este vehiculo?')) return;
    try {
      await axios.delete(`${API}/generic-vehicles/${id}`, { withCredentials: true });
      toast.success('Vehiculo eliminado');
      fetchData();
    } catch { toast.error('Error al eliminar'); }
  };

  const handleImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setIsImporting(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const { data } = await axios.post(`${API}/generic-vehicles/import/excel`, formData, {
        withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(`Importados: ${data.imported} vehiculos`);
      if (data.errors.length > 0) {
        toast.warning(`${data.errors.length} errores: ${data.errors.slice(0, 3).join(', ')}`);
      }
      if (page !== 1) setPage(1);
      else fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al importar');
    } finally {
      setIsImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  return (
    <div className="min-h-screen bg-slate-900 flex flex-col">
      <header className="bg-slate-800 border-b border-slate-700">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14 sm:h-16">
            <div className="flex items-center gap-2 sm:gap-4">
              <Link to="/dashboard">
                <Button variant="ghost" size="sm" className="text-slate-300 hover:text-white">
                  <ArrowLeft className="w-4 h-4 sm:mr-2" /><span className="hidden sm:inline">Volver</span>
                </Button>
              </Link>
              <div className="border-l border-slate-700 pl-2 sm:pl-4">
                <h1 className="text-base sm:text-lg font-bold text-white">Vehiculos</h1>
                <p className="text-xs text-slate-400 hidden sm:block">Autos y motos registrados (no socios)</p>
              </div>
            </div>
            <img src={LOGO_URL} alt="ParkingVision AI" className="h-6 sm:h-8 w-auto" />
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-8 flex-1">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 sm:mb-6 gap-3">
          <div className="flex items-center gap-2">
            <div className="relative flex-1 sm:w-64">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input value={searchTerm} onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
                placeholder="Buscar..." className="bg-slate-800 border-slate-700 text-white pl-10" data-testid="search-vehicles" />
            </div>
            <Badge className="bg-blue-600 whitespace-nowrap">{total}</Badge>
          </div>
          <div className="flex gap-2 flex-wrap">
            <a href={`${API}/generic-vehicles/export/excel`} data-testid="export-vehicles-excel">
              <Button variant="outline" size="sm" className="border-slate-700 text-slate-300 hover:bg-slate-700">
                <Download className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">Excel</span>
              </Button>
            </a>
            <input type="file" ref={fileInputRef} accept=".xlsx,.xls" onChange={handleImport} className="hidden" data-testid="import-vehicles-input" />
            <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} disabled={isImporting}
              className="border-slate-700 text-slate-300 hover:bg-slate-700" data-testid="import-vehicles-btn">
              {isImporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4 sm:mr-1" />}<span className="hidden sm:inline">Importar</span>
            </Button>
            <Button size="sm" onClick={openCreate} className="bg-emerald-600 hover:bg-emerald-700" data-testid="add-vehicle-btn">
              <Plus className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">Agregar</span>
            </Button>
          </div>
        </div>

        <Card className="bg-slate-800 border-slate-700">
          <CardContent className="p-0">
            {isLoading ? (
              <div className="text-center py-12 text-slate-400">Cargando...</div>
            ) : vehicles.length === 0 ? (
              <div className="text-center py-12 text-slate-400">
                <Truck className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>{searchTerm ? 'No se encontraron vehiculos' : 'No hay vehiculos registrados'}</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="vehicles-table">
                  <thead>
                    <tr className="border-b border-slate-700">
                      <th className="text-left py-3 px-4 text-slate-400 font-medium">Patente</th>
                      <th className="text-left py-3 px-4 text-slate-400 font-medium">Tipo</th>
                      <th className="text-left py-3 px-4 text-slate-400 font-medium">Propietario</th>
                      <th className="text-left py-3 px-4 text-slate-400 font-medium">Telefono</th>
                      <th className="text-left py-3 px-4 text-slate-400 font-medium">Notas</th>
                      <th className="text-right py-3 px-4 text-slate-400 font-medium">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {vehicles.map((v) => (
                      <tr key={v.id} className="border-b border-slate-700/50 hover:bg-slate-700/30" data-testid={`vehicle-row-${v.id}`}>
                        <td className="py-3 px-4">
                          <span className="font-mono font-bold text-white bg-slate-700 px-2 py-1 rounded">{v.plate}</span>
                        </td>
                        <td className="py-3 px-4">
                          <Badge variant="outline" className={v.vehicle_type === 'moto' ? 'text-amber-400 border-amber-400/50' : 'text-blue-400 border-blue-400/50'}>
                            {v.vehicle_type === 'moto' ? <Bike className="w-3 h-3 mr-1" /> : <Car className="w-3 h-3 mr-1" />}
                            {v.vehicle_type === 'moto' ? 'Moto' : 'Auto'}
                          </Badge>
                        </td>
                        <td className="py-3 px-4 text-slate-300">{v.owner_name || '-'}</td>
                        <td className="py-3 px-4 text-slate-300">{v.phone || '-'}</td>
                        <td className="py-3 px-4 text-slate-400 text-sm max-w-[200px] truncate">{v.notes || '-'}</td>
                        <td className="py-3 px-4 text-right">
                          <div className="flex gap-1 justify-end">
                            <Button variant="ghost" size="sm" onClick={() => openEdit(v)} className="text-blue-400 hover:text-blue-300 h-8 w-8 p-0">
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleDelete(v.id)} className="text-red-400 hover:text-red-300 h-8 w-8 p-0">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {!isLoading && total > 0 && (
          <div className="mt-4 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm">
            <span className="text-slate-400">
              Mostrando {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} de {total}
            </span>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(current => Math.max(1, current - 1))}
                disabled={page <= 1}
                className="border-slate-700 text-slate-300"
                data-testid="vehicles-prev-page"
              >
                <ChevronLeft className="w-4 h-4 mr-1" />Anterior
              </Button>
              <span className="text-slate-300 whitespace-nowrap">Página {page} de {totalPages}</span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(current => Math.min(totalPages, current + 1))}
                disabled={page >= totalPages}
                className="border-slate-700 text-slate-300"
                data-testid="vehicles-next-page"
              >
                Siguiente<ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        )}
      </main>

      <footer className="py-3 sm:py-4 border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-3 flex flex-col sm:flex-row items-center justify-between gap-1">
          <img src={DISRUPTIVE_LOGO} alt="Disruptive Software" className="h-7 w-auto invert" />
          <span className="text-slate-500 text-xs">Desarrollado por <a href="https://www.disruptive-sw.com" target="_blank" rel="noopener noreferrer" className="underline hover:text-white">Disruptive Software</a>. Todos los derechos reservados.</span><span className="text-slate-500 text-xs ml-auto"><a href="mailto:info@disruptive-sw.com" className="hover:text-white">info@disruptive-sw.com</a></span>
        </div>
      </footer>

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2">
              <Car className="w-5 h-5 text-emerald-400" />
              {editingVehicle ? 'Editar Vehiculo' : 'Nuevo Vehiculo'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-slate-300 text-sm">Patente</Label>
              <Input value={form.plate} onChange={(e) => setForm({ ...form, plate: e.target.value.toUpperCase() })}
                placeholder="ABC123" className="bg-slate-700 border-slate-600 text-white font-mono mt-1 uppercase" data-testid="vehicle-plate-input" />
            </div>
            <div>
              <Label className="text-slate-300 text-sm">Tipo de Vehiculo</Label>
              <Select value={form.vehicle_type} onValueChange={(v) => setForm({ ...form, vehicle_type: v })}>
                <SelectTrigger className="bg-slate-700 border-slate-600 text-white mt-1" data-testid="vehicle-type-input">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  <SelectItem value="auto">Auto</SelectItem>
                  <SelectItem value="moto">Moto</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-slate-300 text-sm">Propietario (opcional)</Label>
              <Input value={form.owner_name} onChange={(e) => setForm({ ...form, owner_name: e.target.value })}
                className="bg-slate-700 border-slate-600 text-white mt-1" data-testid="vehicle-owner-input" />
            </div>
            <div>
              <Label className="text-slate-300 text-sm">Telefono (opcional)</Label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className="bg-slate-700 border-slate-600 text-white mt-1" data-testid="vehicle-phone-input" />
            </div>
            <div>
              <Label className="text-slate-300 text-sm">Notas (opcional)</Label>
              <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                className="bg-slate-700 border-slate-600 text-white mt-1" data-testid="vehicle-notes-input" />
            </div>
            <div className="flex gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowModal(false)} className="flex-1 border-slate-600 text-slate-300">Cancelar</Button>
              <Button onClick={handleSave} disabled={isSaving} className="flex-1 bg-emerald-600 hover:bg-emerald-700" data-testid="save-vehicle-btn">
                {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Guardar'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
